import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Slider from '@mui/material/Slider';
import React from 'react';

import Button from '../../././Button';
import { SHADOW_LIGHT } from '../../../theme/base/shadows';
import { BreakPoints } from '../../../theme/types';
import { InputRenderer } from '../types';

import HiddenInput from './HiddenInput';

const HALF = 0.5;
const SliderSize = 8;
const ThumbFontSize = 20;
const ThumbOpacity = 0.2;
const ThumpSizeModifier = 3;
const ThumbSize = SliderSize * ThumpSizeModifier;

const getAriaValueText = (v: number) => (Number.isNaN(v) ? '⭤' : v.toString());

const SliderInput = ({ inputProps, maxInclusive, minInclusive, required }: InputRenderer) => {
  const theme = useTheme();
  const present = !!inputProps.value;

  const handleReset: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();
    inputProps.onChange('');
  };

  const handleChange = (e: Event, value: number) => {
    e.preventDefault();
    inputProps.onChange(value);
  };

  if (maxInclusive === undefined || minInclusive === undefined) {
    return null;
  }

  const defaultMarks = [
    {
      label: minInclusive,
      value: minInclusive,
    },
    {
      label: maxInclusive,
      value: maxInclusive,
    },
  ];
  const defaultValue = minInclusive + (maxInclusive - minInclusive) * HALF;

  return (
    <React.Fragment>
      <Slider
        aria-labelledby="discrete-slider-custom"
        getAriaValueText={getAriaValueText}
        marks={defaultMarks}
        max={maxInclusive}
        min={minInclusive}
        sx={{
          '& .MuiSlider-mark': {
            '&:not(&[data-index="0"])': {
              transform: `translateY(-${SliderSize}px) translateX(-${SliderSize}px)`,
            },
            backgroundColor: theme.palette.grey.xLight,
            borderRadius: '50%',
            height: `calc(${SliderSize}px * 2)`,
            transform: 'translateY(-8px)',
            width: `calc(${SliderSize}px * 2)`,
          },
          '& .MuiSlider-markLabel': {
            [theme.breakpoints.down(BreakPoints.Medium)]: {
              '&[data-index="1"]': {
                transform: 'translateX(-100%)',
              },
              transform: 'translateX(0%)',
            },
            fontWeight: theme.typography.fontWeightBold,
            marginTop: '.5rem',
          },
          '& .MuiSlider-rail': {
            backgroundColor: theme.palette.grey.xLight,
            borderRadius: theme.shape.borderRadius,
            height: SliderSize,
            opacity: 1,
          },
          '& .MuiSlider-thumb': {
            '&:hover': {
              fontSize: present ? undefined : ThumbFontSize,
            },
            height: ThumbSize,
            left: present ? undefined : '50%',
            opacity: present ? undefined : ThumbOpacity,
            width: ThumbSize,
          },
          filter: `drop-shadow(${SHADOW_LIGHT})`,
          height: SliderSize,
          marginTop: '.5rem',
        }}
        track={false}
        value={typeof inputProps.value === 'number' ? inputProps.value : defaultValue}
        valueLabelDisplay="auto"
        valueLabelFormat={getAriaValueText}
        onChange={handleChange}
      />
      {!required && (
        <Box
          sx={{
            float: 'right',
            marginTop: '1em',
          }}
        >
          <Button small disabled={inputProps.value === ''} type="button" variant="outlined" onClick={handleReset}>
            reset
          </Button>
        </Box>
      )}
      {required && <HiddenInput inputProps={inputProps} required={required} />}
    </React.Fragment>
  );
};

export default SliderInput;
